import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { observer } from "mobx-react";
import { Box, Paper, Grid2, CircularProgress, Typography } from "@mui/material";
import FormTextField from "../../../../../views/components/common/form/FormTextField";
import FormTitle from "../../../../../views/components/common/form/FormTitle";
import AppButton from "../../../../../views/components/common/buttons/AppButton";
import AppDropDown from "../../../../../views/components/common/filter/AppDropDown";
import Switch from "../../../../../views/components/common/switch/Switch";
import MediaAssetsUpload from "../../../../../views/components/common/upload/MediaAssetsUpload";
import PageTitle from "../../../../../views/components/common/pages/PageTitle";
import { useNotification } from "../../../../../utils/NotificationsContext";
import { useStores } from "../../../use-stores";
import { FilterValue } from "../../../../../models/Filters/FilterValue";
import {
  validatePropertyForm,
  type PropertyCreationFormValues,
  type PropertyCreateRequestBody,
} from "./propertyCreationSchema";
import AddNewAmenityModal from "../../components/property/AddNewAmenityModal";
import ImageAPI from "../../../api/ImageAPI";
import AppSearchDropdown2 from "../../../../../views/components/common/filter/AppSearchDropdown2";
import { City } from "../../../../../models/Property/Property";
import { ImageData } from "../../../../../models/Image/Image";
import type { PropertyDetails } from "../../../../../models/Property/Property";

export interface ExistingPropertyImage {
  id?: number;
  image_url: string;
  image_scope?: string;
  category_id?: number;
  is_primary?: boolean;
  sort_order?: number;
}

const ADD_NEW_AMENITY_OPTION: FilterValue = {
  id: "__add_new__",
  value: "Add New Amenity",
};

const CreateProperty: React.FC = () => {
  const { id: routeId } = useParams<{ id: string }>();
  const isEditMode = Boolean(routeId);
  const propertyId = routeId ? parseInt(routeId, 10) : null;

  const { showError, showSuccess } = useNotification();
  const { propertyStore, addressStore } = useStores();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [formPopulated, setFormPopulated] = useState(false);
  const [existingPropertyImages, setExistingPropertyImages] = useState<ExistingPropertyImage[]>([]);

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [address, setAddress] = useState("");
  const [cityId, setCityId] = useState<string | null>(null);
  const [cityDisplay, setCityDisplay] = useState("");
  const [originalPrice, setOriginalPrice] = useState<string | number>("");
  const [discountedPrice, setDiscountedPrice] = useState<string | number>("");
  const [totalRooms, setTotalRooms] = useState<string | number>("");
  const [capacity, setCapacity] = useState<string | number>("");
  const [highlight, setHighlight] = useState("");
  const [driveableDistance, setDriveableDistance] = useState("");
  const [display, setDisplay] = useState(true);
  const [eventTypeId, setEventTypeId] = useState<string | null>(null);
  const [propertyCategoryId, setPropertyCategoryId] = useState<string | null>(null);
  const [propertyTypeId, setPropertyTypeId] = useState<string | null>(null);
  const [amenityIds, setAmenityIds] = useState<string[]>([]);
  const [propertyImages, setPropertyImages] = useState<File[]>([]);

  const [addAmenityModalOpen, setAddAmenityModalOpen] = useState(false);
  const [addAmenityLoading, setAddAmenityLoading] = useState(false);
  const citySearchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cities: FilterValue[] = propertyStore.cities.map((c) => ({
    id: String(c.id),
    value: c.name ?? "",
  }));
  const eventTypes: FilterValue[] = propertyStore.eventTypes.map((c) => ({
    id: String(c.id),
    value: c.label ?? c.name ?? "",
  }));
  const propertyCategories: FilterValue[] = propertyStore.propertyCategories.map(
    (c) => ({
      id: String(c.id),
      value: c.label ?? c.name ?? "",
    })
  );
  const propertyTypes: FilterValue[] = propertyStore.propertyTypes.map((c) => ({
    id: String(c.id),
    value: c.label ?? c.name ?? "",
  }));
  const amenities: FilterValue[] = propertyStore.amenities.map((c) => ({
    id: String(c.id),
    value: c.label ?? c.name ?? "",
  }));

  const loadCities = useCallback(
    (searchText?: string) => {
      addressStore
        ?.getCity({ page_no: 1, limit: 50, search_text: searchText, active: true })
        .then((list) => {
          propertyStore.setCities(Array.isArray(list) ? list.map((c) => ({ id: c.id ?? 0, name: c.name ?? "" })) : [] as City[]);
        })
        .catch(() => { });
    },
    [addressStore, propertyStore]
  );

  useEffect(() => {
    loadCities();
  }, [loadCities]);

  useEffect(() => {
    propertyStore.getEventTypes({ page_no: 1, limit: 100, active: true }).catch(() => { });
  }, [propertyStore]);

  useEffect(() => {
    propertyStore
      .getPropertyCategories({ page_no: 1, limit: 100, active: true })
      .catch(() => { });
  }, [propertyStore]);

  useEffect(() => {
    propertyStore.getPropertyTypes({ page_no: 1, limit: 100, active: true }).catch(() => { });
  }, [propertyStore]);

  useEffect(() => {
    propertyStore.getAmenities({ page_no: 1, limit: 200, active: true }).catch(() => { });
  }, [propertyStore]);

  useEffect(() => {
    if (!isEditMode) {
      propertyStore.clearPropertyDetails();
    }
  }, [isEditMode, propertyStore]);

  // When navigating to a different property (id change), reset so new data can populate
  useEffect(() => {
    if (isEditMode && propertyId != null && !isNaN(propertyId)) {
      propertyStore.clearPropertyDetails();
      setFormPopulated(false);
    }
  }, [propertyId, isEditMode, propertyStore]);

  useEffect(() => {
    if (!isEditMode || !propertyId || isNaN(propertyId)) return;
    propertyStore
      .getPropertyById(propertyId)
      .then(() => { })
      .catch((err: unknown) => {
        showError(
          "Error",
          (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
          "Failed to load property"
        );
      });
  }, [isEditMode, propertyId, propertyStore, showError]);

  useEffect(() => {
    const d = propertyStore.propertyDetails as PropertyDetails | null;
    if (!isEditMode || !d || formPopulated) return;
    setName(d.name ?? "");
    setDescription(d.description ?? "");
    setAddress(d.address ?? "");
    const cid = d.city_id ?? (typeof d.city === "object" && d.city?.id != null ? d.city.id : null);
    const cityDisplayName =
      typeof d.city === "string"
        ? d.city
        : (typeof d.city === "object" ? d.city?.name : undefined) ?? (cid != null ? String(cid) : "");
    if (cid != null) {
      setCityId(String(cid));
      setCityDisplay(cityDisplayName || String(cid));
    }
    setOriginalPrice(d.original_price ?? "");
    setDiscountedPrice(d.discounted_price ?? "");
    setTotalRooms(d.total_rooms ?? "");
    setCapacity(d.capacity ?? "");
    setHighlight(d.highlight ?? "");
    setDriveableDistance(d.driveable_distance ?? "");
    setDisplay(d.display === 1);
    const pt = Array.isArray(d.property_type) ? d.property_type : [];
    const firstType = pt.length ? (typeof pt[0] === "object" ? (pt[0] as { id: number }).id : pt[0]) : null;
    if (firstType != null) setPropertyTypeId(String(firstType));
    const pc = Array.isArray(d.property_category) ? d.property_category : [];
    const firstCat = pc.length ? (typeof pc[0] === "object" ? (pc[0] as { id: number }).id : pc[0]) : null;
    if (firstCat != null) setPropertyCategoryId(String(firstCat));
    const et = Array.isArray(d.event_types) ? d.event_types : [];
    const firstEvent = et.length ? (typeof et[0] === "object" ? (et[0] as { id: number }).id : et[0]) : null;
    if (firstEvent != null) setEventTypeId(String(firstEvent));
    const am = Array.isArray(d.amenities) ? d.amenities : [];
    setAmenityIds(am.map((a) => String(typeof a === "object" ? (a as { id: number }).id : a)));

    const imgs = d.images ?? d.property_images ?? [];
    const normalizedImgs = Array.isArray(imgs) ? imgs : [];
    setExistingPropertyImages(
      normalizedImgs
        .filter((img) => (typeof img === "string" && img) || (typeof img === "object" && img !== null && "image_url" in img && (img as { image_url: string }).image_url))
        .map((img) => {
          const item = typeof img === "object" && img !== null && "image_url" in img
            ? (img as { id?: number; image_url: string; image_scope?: string; category_id?: number; is_primary?: number | boolean; sort_order?: number })
            : { image_url: typeof img === "string" ? img : "", image_scope: "PROPERTY" as const, category_id: 6, is_primary: false, sort_order: 0 };
          return {
            ...(item.id != null && { id: item.id }),
            image_url: item.image_url,
            image_scope: item.image_scope ?? "PROPERTY",
            category_id: item.category_id ?? 6,
            is_primary: item.is_primary === 1 || item.is_primary === true,
            sort_order: typeof item.sort_order === "number" ? item.sort_order : 0,
          };
        })
    );
    setFormPopulated(true);
  }, [isEditMode, propertyStore.propertyDetails, formPopulated]);

  const handleCitySearch = useCallback(
    (searchText?: string) => {
      if (citySearchTimerRef.current) clearTimeout(citySearchTimerRef.current);
      const timer = setTimeout(() => loadCities(searchText), 400);
      citySearchTimerRef.current = timer;
      return () => clearTimeout(timer);
    },
    [loadCities]
  );

  const handleCitySelect = (items: FilterValue[]) => {
    if (items.length === 0) {
      setCityId(null);
      setCityDisplay("");
    } else {
      setCityId(items[0].id);
      setCityDisplay(items[0].value);
    }
    setFieldErrors((e) => ({ ...e, city_id: "" }));
  };

  const amenitiesWithAddNew = [...amenities, ADD_NEW_AMENITY_OPTION];
  const selectedAmenitiesFilter: FilterValue[] = amenityIds.map((id) => {
    const a = amenities.find((x) => x.id === id);
    return a ? { id: a.id, value: a.value } : { id, value: String(id) };
  });

  const handleAmenitySelect = (items: FilterValue[]) => {
    const addNewItem = items.find((i) => i.id === ADD_NEW_AMENITY_OPTION.id);
    if (addNewItem) {
      setAddAmenityModalOpen(true);
      const rest = items.filter((i) => i.id !== ADD_NEW_AMENITY_OPTION.id);
      setAmenityIds(rest.map((i) => i.id));
    } else {
      setAmenityIds(items.map((i) => i.id));
    }
  };

  const handleAddNewAmenitySubmit = (amenityName: string) => {
    setAddAmenityLoading(true);
    propertyStore
      .createAmenity(amenityName)
      .then((res: any) => {
        const created = res?.result;
        if (created && created.id != null) {
          setAmenityIds((prev) => [...prev, String(created.id)]);
          propertyStore.getAmenities({ page_no: 1, limit: 200, active: true }).catch(() => { });
          showSuccess("Amenity added successfully");
        }
      })
      .catch((err: any) => {
        showError(
          "Error",
          err?.response?.data?.message || "Failed to create amenity"
        );
      })
      .finally(() => setAddAmenityLoading(false));
  };

  const addImages = (files: File[]) => {
    setPropertyImages((prev) => [...prev, ...files]);
    setFieldErrors((e) => ({ ...e, property_images: "" }));
  };

  const removeNewImage = (image: ImageData) => {
    setPropertyImages((prev) =>
      prev.filter((f) => f.name !== image.title)
    );
  };

  const validate = (): PropertyCreationFormValues | null => {
    const result = validatePropertyForm(
      {
        name,
        description,
        address,
        city_id: cityId,
        original_price: originalPrice,
        discounted_price: discountedPrice,
        total_rooms: totalRooms,
        capacity,
        highlight,
        driveable_distance: driveableDistance,
        display,
        event_type_ids: eventTypeId ? [eventTypeId] : [],
        property_category_id: propertyCategoryId,
        property_type_id: propertyTypeId,
        amenity_ids: amenityIds,
        property_images: propertyImages,
      },
      { existingImageCount: isEditMode ? existingPropertyImages.length : 0 }
    );

    if (result.valid) {
      setFieldErrors({});
      return result.data;
    }
    setFieldErrors(result.errors);
    return null;
  };

  const removeExistingImage = (image: ImageData) => {
    const url = image.img;
    setExistingPropertyImages((prev) => prev.filter((p) => p.image_url !== url));
  };

  const handleSubmit = async () => {
    const data = validate();
    if (!data) return;

    setIsSubmitting(true);
    let newImageUrls: string[] = [];
    if (propertyImages.length > 0) {
      try {
        const imageAPI = new ImageAPI();
        newImageUrls = await imageAPI.uploadWebpImages(propertyImages, "event-management");
      } catch (err: unknown) {
        showError(
          "Error",
          (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
          "Failed to upload images"
        );
        setIsSubmitting(false);
        return;
      }
    }

    const existingImagesPayload = existingPropertyImages.map((img, i) => ({
      ...(img.id != null && { id: img.id }),
      image_url: img.image_url,
      image_scope: (img.image_scope as "PROPERTY" | "ROOM") || "PROPERTY",
      category_id: img.category_id ?? 6,
      is_primary: img.is_primary ?? i === 0,
      sort_order: img.sort_order ?? i,
    }));
    const newImagesPayload = newImageUrls.map((image_url, i) => ({
      image_url,
      image_scope: "PROPERTY" as const,
      category_id: 6,
      is_primary: existingImagesPayload.length === 0 && i === 0,
      sort_order: existingImagesPayload.length + i,
    }));
    const allImages = [...existingImagesPayload, ...newImagesPayload];
    if (allImages.length < 5) {
      showError("Error", "Minimum 5 images are required (existing + new)");
      setIsSubmitting(false);
      return;
    }

    const body: PropertyCreateRequestBody = {
      name: data.name,
      description: data.description ?? null,
      address: data.address ?? null,
      city_id: data.city_id,
      original_price: data.original_price ?? null,
      discounted_price: data.discounted_price ?? null,
      total_rooms: data.total_rooms ?? null,
      capacity: data.capacity ?? null,
      highlight: data.highlight ?? null,
      driveable_distance: data.driveable_distance ?? null,
      display: data.display ? 1 : 0,
      property_type: data.property_type_id != null ? [data.property_type_id] : [],
      property_category: [data.property_category_id],
      event_types: data.event_type_ids?.length ? data.event_type_ids : [],
      amenities: data.amenity_ids?.length ? data.amenity_ids : [],
      property_images: allImages,
    };

    if (isEditMode && propertyId != null) {
      propertyStore
        .updateProperty(propertyId, body as unknown as Record<string, unknown>)
        .then(() => {
          showSuccess("Property updated successfully");
        })
        .catch((err: unknown) => {
          showError(
            "Error",
            (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
            "Failed to update property"
          );
        })
        .finally(() => setIsSubmitting(false));
    } else {
      propertyStore
        .createProperty(body as unknown as Record<string, unknown>)
        .then(() => {
          showSuccess("Property created successfully");
          resetForm();
        })
        .catch((err: unknown) => {
          showError(
            "Error",
            (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
            "Failed to create property"
          );
        })
        .finally(() => setIsSubmitting(false));
    }
  };

  const resetForm = () => {
    setFormPopulated(false);
    setExistingPropertyImages([]);
    setName("");
    setDescription("");
    setAddress("");
    setCityId(null);
    setCityDisplay("");
    setOriginalPrice("");
    setDiscountedPrice("");
    setTotalRooms("");
    setCapacity("");
    setHighlight("");
    setDriveableDistance("");
    setDisplay(true);
    setEventTypeId(null);
    setPropertyCategoryId(null);
    setPropertyTypeId(null);
    setAmenityIds([]);
    setPropertyImages([]);
    setFieldErrors({});
  };


  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: "100%",
        overflow: "hidden",
        boxSizing: "border-box",
        px: { xs: 1, sm: 2 },
        py: 2,
      }}
    >
      <PageTitle title={isEditMode ? "Edit Property" : "Create Property"} />
      <Grid2 container spacing={3} columns={{ xs: 12, sm: 12, md: 12 }}>
        <Grid2 size={{ xs: 12 }}>
          <Paper
            elevation={1}
            sx={{
              p: { xs: 2, sm: 3, md: 4 },
              overflow: "hidden",
              maxWidth: "100%",
              boxSizing: "border-box",
            }}
          >
            <Grid2 container spacing={3} columns={{ xs: 12, sm: 12, md: 12 }}>
              {/* Row 1: Name, Price */}
              <Grid2 size={{ xs: 12, sm: 12, md: 6 }}>
                <FormTitle title="Name" fontSize="12px" />
                <FormTextField
                  required
                  label="Property name"
                  value={name}
                  onChange={(_id, value) => {
                    setName(value ?? "");
                    if (fieldErrors.name) setFieldErrors((e) => ({ ...e, name: "" }));
                  }}
                  error={Boolean(fieldErrors.name)}
                  errorMessage={fieldErrors.name}
                  fontSize="13px"
                />
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6, md: 6 }}>
                <FormTitle title="Original price" fontSize="12px" />
                <FormTextField
                  type="number"
                  label="Original price"
                  value={originalPrice}
                  onChange={(_id, value) => {
                    setOriginalPrice(value ?? "");
                    if (fieldErrors.original_price) setFieldErrors((e) => ({ ...e, original_price: "" }));
                  }}
                  error={Boolean(fieldErrors.original_price)}
                  errorMessage={fieldErrors.original_price}
                  fontSize="13px"
                />
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6, md: 6 }}>
                <FormTitle title="Discounted price" fontSize="12px" />
                <FormTextField
                  type="number"
                  label="Discounted price"
                  value={discountedPrice}
                  onChange={(_id, value) => {
                    setDiscountedPrice(value ?? "");
                    if (fieldErrors.discounted_price) setFieldErrors((e) => ({ ...e, discounted_price: "" }));
                  }}
                  error={Boolean(fieldErrors.discounted_price)}
                  errorMessage={fieldErrors.discounted_price}
                  fontSize="13px"
                />
              </Grid2>

              {/* Row 2: Description (full width) */}
              <Grid2 size={{ xs: 12 }}>
                <FormTitle title="Description" fontSize="12px" />
                <FormTextField
                  multiline
                  rows={3}
                  label="Description"
                  value={description}
                  onChange={(_id, value) => setDescription(value ?? "")}
                  fontSize="13px"
                />
              </Grid2>

              {/* Row 3: Address, City */}
              <Grid2 size={{ xs: 12, sm: 12, md: 6 }}>
                <FormTitle title="Address" fontSize="12px" />
                <FormTextField
                  label="Address"
                  value={address}
                  onChange={(_id, value) => setAddress(value ?? "")}
                  fontSize="13px"
                />
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 12, md: 6 }}>
                <FormTitle title="City" fontSize="12px" />
                <AppSearchDropdown2
                  
                  isMultiSelect={false}
                  title="select city*"
                  value={
                    cityId ? [{ id: cityId, value: cityDisplay }] : []
                  }
                  list={cities}
                  local={false}
                  handleSearch={handleCitySearch}
                  handleSelect={handleCitySelect}
                  disabled={false}
                  fontSize="13px"
                />
                {fieldErrors.city_id && (
                  <Typography
                    variant="caption"
                    sx={{ color: "#AF095E", fontSize: "12px", mt: 0.5, display: "block" }}
                  >
                    {fieldErrors.city_id}
                  </Typography>
                )}
              </Grid2>

              {/* Row 4: Total rooms, Capacity, Driveable distance */}
              <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                <FormTitle title="Total rooms" fontSize="12px" />
                <FormTextField
                  type="number"
                  label="Total rooms"
                  value={totalRooms}
                  onChange={(_id, value) => {
                    setTotalRooms(value ?? "");
                    if (fieldErrors.total_rooms)
                      setFieldErrors((e) => ({ ...e, total_rooms: "" }));
                  }}
                  error={Boolean(fieldErrors.total_rooms)}
                  errorMessage={fieldErrors.total_rooms}
                  fontSize="13px"
                />
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                <FormTitle title="Capacity" fontSize="12px" />
                <FormTextField
                  type="number"
                  label="Capacity"
                  value={capacity ? capacity : ""}
                  onChange={(_id, value) => {
                    setCapacity(value ?? "");
                    if (fieldErrors.capacity)
                      setFieldErrors((e) => ({ ...e, capacity: "" }));
                  }}
                  error={Boolean(fieldErrors.capacity)}
                  errorMessage={fieldErrors.capacity}
                  fontSize="13px"
                />
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                <FormTitle title="Driveable distance" fontSize="12px" />
                <FormTextField
                  label="Driveable distance (e.g. 5 km)"
                  value={driveableDistance}
                  onChange={(_id, value) => {
                    setDriveableDistance(value ?? "");
                    if (fieldErrors.driveable_distance)
                      setFieldErrors((e) => ({ ...e, driveable_distance: "" }));
                  }}
                  error={Boolean(fieldErrors.driveable_distance)}
                  errorMessage={fieldErrors.driveable_distance}
                  fontSize="13px"
                />
              </Grid2>

              {/* Row 5: Highlight */}
              <Grid2 size={{ xs: 12 }}>
                <FormTitle title="Highlight" fontSize="12px" />
                <FormTextField
                  label="Highlight"
                  value={highlight}
                  onChange={(_id, value) => setHighlight(value ?? "")}
                  fontSize="13px"
                />
              </Grid2>

              {/* Row 6: Display, Event type, Property category, Property type */}
              <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                <FormTitle title="Display" fontSize="12px" />
                <Box sx={{ display: "flex", alignItems: "center", minHeight: 56 }}>
                  <Switch
                    checked={display}
                    onChange={(e) => setDisplay((e.target as HTMLInputElement).checked)}
                  />
                </Box>
                {fieldErrors.display && (
                  <Typography
                    variant="caption"
                    sx={{ color: "#AF095E", fontSize: "12px", mt: 0.5, display: "block" }}
                  >
                    {fieldErrors.display}
                  </Typography>
                )}
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                <FormTitle title="Event type" fontSize="12px" />
                <AppDropDown
                  isMultiSelect={false}
                  title="Event type"
                  list={eventTypes}
                  value={eventTypeId ?? ""}
                  onSingleSelectionChange={(id) => setEventTypeId(id || null)}
                  id="event_type"
                  fontSize="13px"
                  placeholderText="Select event type"
                />
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                <FormTitle title="Property category" fontSize="12px" />
                <AppDropDown
                  isMultiSelect={false}
                  required
                  title="Property category"
                  list={propertyCategories}
                  value={propertyCategoryId ?? ""}
                  onSingleSelectionChange={(id) => {
                    setPropertyCategoryId(id || null);
                    if (fieldErrors.property_category_id) setFieldErrors((e) => ({ ...e, property_category_id: "" }));
                  }}
                  id="property_category"
                  fontSize="13px"
                  placeholderText="Select category"
                />
                {fieldErrors.property_category_id && (
                  <Typography variant="caption" sx={{ color: "#AF095E", fontSize: "12px", mt: 0.5, display: "block" }}>
                    {fieldErrors.property_category_id}
                  </Typography>
                )}
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                <FormTitle title="Property type" fontSize="12px" />
                <AppDropDown
                  isMultiSelect={false}
                  title="Property type"
                  list={propertyTypes}
                  value={propertyTypeId ?? ""}
                  onSingleSelectionChange={(id) => {
                    setPropertyTypeId(id || null);
                    if (fieldErrors.property_type_id) setFieldErrors((e) => ({ ...e, property_type_id: "" }));
                  }}
                  id="property_type"
                  fontSize="13px"
                  placeholderText="Select type"
                />
                {fieldErrors.property_type_id && (
                  <Typography variant="caption" sx={{ color: "#AF095E", fontSize: "12px", mt: 0.5, display: "block" }}>
                    {fieldErrors.property_type_id}
                  </Typography>
                )}
              </Grid2>

              {/* Row 7: Amenities (multi-select + Add New) */}
              <Grid2 size={{ xs: 12 }}>
                <FormTitle title="Amenities" fontSize="12px" />
                <AppSearchDropdown2
                  isMultiSelect={true}
                  title="Select amenities"
                  list={amenitiesWithAddNew}
                  value={selectedAmenitiesFilter}
                  handleSelect={handleAmenitySelect}
                  local={true}
                  fontSize="13px"
                  showSelection={true}
                />
              </Grid2>

              {/* Row 8: Property images */}
              <Grid2 size={{ xs: 12 }}>
                <FormTitle title="Property images (min 5)" fontSize="12px" />
                <MediaAssetsUpload
                  existingImageList={existingPropertyImages.map((img, i) => ({
                    id: i,
                    img: img.image_url,
                    title: `Image ${i + 1}`,
                    is_default: img.is_primary ?? false,
                  }))}
                  newImageList={propertyImages}
                  deleteImage={(image: ImageData) => {
                    removeExistingImage(image);
                  }}
                  removeImage={(image: { id: string | number; title?: string; img: string }) => {
                    setPropertyImages((prev) =>
                      prev.filter((f) => f.name !== image.title)
                    );
                  }}
                  addImagesToBeUploaded={(files: File[]) => addImages(files)}
                  fileInfo="Min 5 images · Webp"
                  uploadText="Drop images here or browse"
                />
                {fieldErrors.property_images && (
                  <Typography
                    variant="caption"
                    sx={{
                      color: "#AF095E",
                      fontSize: "12px",
                      mt: 1,
                      display: "block",
                    }}
                  >
                    {fieldErrors.property_images}
                  </Typography>
                )}
              </Grid2>

              {/* Actions */}
              <Grid2 size={{ xs: 12 }}>
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 2,
                    mt: 2,
                    justifyContent: "flex-start",
                  }}
                >
                  <AppButton
                    label={
                      isSubmitting ? (
                        <>
                          <CircularProgress size={14} color="inherit" />
                          {isEditMode ? "Saving..." : "Creating..."}
                        </>
                      ) : isEditMode ? (
                        "Save"
                      ) : (
                        "Create Property"
                      )
                    }
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                  />
                  {!isEditMode && (
                    <AppButton
                      label="Clear"
                      variant="grayscale"
                      onClick={resetForm}
                      disabled={isSubmitting}
                    />
                  )}
                </Box>
              </Grid2>
            </Grid2>
          </Paper>
        </Grid2>
      </Grid2>

      <AddNewAmenityModal
        open={addAmenityModalOpen}
        onClose={() => setAddAmenityModalOpen(false)}
        onSubmit={handleAddNewAmenitySubmit}
        loading={addAmenityLoading}
      />
    </Box>
  );
};

export default observer(CreateProperty);
